import { api } from "@/shared/lib/axios/axios.api"
import { API_PREFIX } from "@/shared/lib/axios/axios.constants"

import type {
  CATReviewCategory,
  CATReviewDocumentSettings,
  CATReviewStartRequest,
  CATReviewErrorCode,
  CATReviewPhase,
  CATReviewProposal,
  CATReviewRun,
  CATReviewRunStatus,
} from "./cat-review.types"

/**
 * The run as it reaches this module.
 *
 * The server sends snake_case, but the shared response interceptor camelizes every JSON body
 * before it gets here (`axios.interceptors.ts`, unless a request opts out with
 * `preserveResponseKeys`). So this is declared in camelCase, like every other task-management
 * response type — reading `segment_id` here would quietly be `undefined`.
 */
interface RunResponse {
  run: {
    id: string
    jobId: string
    status: string
    requestedBy: string
    reviewedSegmentCount: number | null
    totalSegmentCount?: number | null
    totalChunkCount?: number | null
    completedChunkCount?: number | null
    reusedChunkCount?: number | null
    phase?: string | null
    proposals: {
      segmentId: string
      category: string
      reason: string
      currentText: string
      proposedText: string
      againstRevision: number
    }[]
    discarded: { segmentId: string | null; cause: string }[]
    errorCode: string | null
    errorMessage: string | null
    createdAt: string
    startedAt: string | null
    completedAt: string | null
    settingsSnapshot?: CATReviewDocumentSettings | null
    promptContractVersion?: number | null
    effectiveConfigHash?: string | null
  } | null
}

const REVIEW_CATEGORIES: CATReviewCategory[] = [
  "accuracy",
  "terminology",
  "register",
  "fluency",
]

const RUN_STATUSES: CATReviewRunStatus[] = [
  "queued",
  "running",
  "completed",
  "failed",
]

const ERROR_CODES: CATReviewErrorCode[] = [
  "not_staff",
  "access_check_failed",
  "unsupported_settings",
  "too_large",
  "output_truncated",
  "unreadable_reply",
  "model_unavailable",
  "workspace_unavailable",
  "abandoned",
]

/**
 * A category this client does not know is shown as `fluency` rather than dropped.
 *
 * The server refuses to emit anything outside its four variants, so this only fires if the two
 * drift — and losing the replacement sentence over a label the reader barely looks at would be the
 * wrong trade.
 */
const toCategory = (value: string): CATReviewCategory =>
  REVIEW_CATEGORIES.includes(value as CATReviewCategory)
    ? (value as CATReviewCategory)
    : "fluency"

/**
 * An unknown status is treated as still running.
 *
 * The alternative is to stop polling on a status this build has never heard of, which would strand
 * a live run on screen as though it had finished with nothing.
 */
const toStatus = (value: string): CATReviewRunStatus =>
  RUN_STATUSES.includes(value as CATReviewRunStatus)
    ? (value as CATReviewRunStatus)
    : "running"

const toErrorCode = (value: string | null): CATReviewErrorCode | null =>
  value !== null && ERROR_CODES.includes(value as CATReviewErrorCode)
    ? (value as CATReviewErrorCode)
    : null

/** Thrown when the document is larger than one review run may carry. */
export class CATReviewTooLargeError extends Error {
  constructor(message?: string) {
    super(message ?? "document is too large to review in one run")
    this.name = "CATReviewTooLargeError"
  }
}

/** Thrown when the account has used its review runs for the day. */
export class CATReviewQuotaError extends Error {
  constructor() {
    super("the daily review run allowance has been used")
    this.name = "CATReviewQuotaError"
  }
}

const deserialize = (data: RunResponse): CATReviewRun | null => {
  const run = data?.run
  if (!run) return null

  return {
    id: String(run.id),
    jobId: String(run.jobId),
    status: toStatus(run.status),
    requestedBy: String(run.requestedBy),
    reviewedSegmentCount: run.reviewedSegmentCount ?? null,
    totalSegmentCount: run.totalSegmentCount ?? null,
    totalChunkCount: run.totalChunkCount ?? null,
    completedChunkCount: run.completedChunkCount ?? null,
    reusedChunkCount: run.reusedChunkCount ?? null,
    phase: ["segments", "consistency", "complete"].includes(run.phase ?? "")
      ? (run.phase as CATReviewPhase)
      : null,
    proposals: (run.proposals ?? []).map(
      (proposal): CATReviewProposal => ({
        // Coerced rather than trusted: segments are looked up by this id, and a mismatch in type
        // is a proposal that silently matches nothing.
        segmentId: String(proposal.segmentId),
        category: toCategory(proposal.category),
        reason: proposal.reason,
        currentText: proposal.currentText,
        proposedText: proposal.proposedText,
        againstRevision: proposal.againstRevision,
      })
    ),
    discarded: (run.discarded ?? []).map((entry) => ({
      segmentId: entry.segmentId === null ? null : String(entry.segmentId),
      cause: entry.cause,
    })),
    errorCode: toErrorCode(run.errorCode),
    errorMessage: run.errorMessage,
    createdAt: run.createdAt,
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    settingsSnapshot: run.settingsSnapshot ?? null,
    promptContractVersion: run.promptContractVersion ?? null,
    effectiveConfigHash: run.effectiveConfigHash ?? null,
  }
}

const statusOf = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null && "response" in error
    ? (error as { response?: { status?: number } }).response?.status
    : undefined

/**
 * Starts a review, or joins the one already in flight.
 *
 * Returns as soon as the run exists. The model call happens on the server and the caller polls —
 * a whole-document review takes longer than any proxy will hold a connection open, and the version
 * that waited threw away an already-paid-for call every time it was cut off.
 */
export const startCatReviewRun = async ({
  jobId,
  request,
}: {
  jobId: string
  request?: CATReviewStartRequest
}): Promise<CATReviewRun | null> => {
  try {
    const url = `${API_PREFIX}/jobs/${jobId}/cat-workspace/review/runs`
    const response = request
      ? await api.post<RunResponse>(url, request, {
          suppressServerErrorAlert: true,
        })
      : await api.post<RunResponse>(url)
    return deserialize(response.data)
  } catch (error) {
    const status = statusOf(error)
    // The two failures with different answers behind them: split the document, or come back
    // tomorrow. Everything else is "it did not work".
    const code = reviewErrorCode(error)
    // Bodyless calls to older servers had an uncoded 400 for size. Coded validation errors differ.
    if (status === 400 && (code === "too_large" || (!request && !code)))
      throw new CATReviewTooLargeError()
    if (status === 429) throw new CATReviewQuotaError()
    throw error
  }
}

/**
 * The most recent run for this job, or `null` if there has never been one.
 *
 * `suppressServerErrorAlert` because this fires on page load and on a timer: nobody asked for it,
 * and a 500 must not raise the shared blocking modal at a translator mid-edit.
 */
export const getLatestCatReviewRun = async ({
  jobId,
}: {
  jobId: string
}): Promise<CATReviewRun | null> => {
  const response = await api.get<RunResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/review/runs/latest`,
    { suppressServerErrorAlert: true }
  )
  return deserialize(response.data)
}

/** Read the exact run, including when a newer run has replaced it in the editor. */
export const getCatReviewRun = async ({
  jobId,
  runId,
}: {
  jobId: string
  runId: string
}) => {
  const response = await api.get<RunResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/review/runs/${runId}`,
    { suppressServerErrorAlert: true }
  )
  return deserialize(response.data)
}

/**
 * Whether the signed-in account may run a review at all.
 *
 * Answers for everyone, so it is safe to call unguarded. The server decides — the list of internal
 * domains stays there, because shipping it to the browser would publish which domains are internal
 * to anyone who opened the bundle.
 */
export const getCATSegmentReviewAccess = async ({
  jobId,
}: {
  jobId: string
}) => {
  const response = await api.get<{
    allowed: boolean
    customizationEnabled?: boolean
    canManageTemplates?: boolean
  }>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/review/access`,
    // Nobody asked for this request. A 500 here must not raise the shared server-error toast at a
    // translator who was editing a document — the caller renders a quiet notice instead.
    { suppressServerErrorAlert: true }
  )
  return response.data
}

export const reviewErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null || !("response" in error))
    return undefined
  return (error as { response?: { data?: { code?: string } } }).response?.data
    ?.code
}

export function reviewSettingsErrorKey(error: unknown) {
  if (error instanceof CATReviewTooLargeError)
    return "mt.reviewProposalTooLarge"
  if (error instanceof CATReviewQuotaError) return "mt.reviewProposalQuota"
  switch (reviewErrorCode(error)) {
    case "settings_revision_conflict":
      return "mt.reviewSettingsConflict"
    case "template_version_conflict":
      return "mt.reviewTemplateConflict"
    case "template_archived":
      return "mt.reviewTemplateArchived"
    case "review_settings_conflict":
      return "mt.reviewSettingsActiveConflict"
    case "idempotency_conflict":
      return "mt.reviewSettingsRequestConflict"
    case "review_customization_unavailable":
      return "mt.reviewSettingsUnavailable"
    case "invalid_review_settings":
      return "mt.reviewSettingsInvalid"
    case "too_large":
      return "mt.reviewProposalTooLarge"
    default:
      return "mt.reviewSettingsFailed"
  }
}
